import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { ArrowRightLeft, FileText, Layers } from "lucide-react";

/**
 * Iter-26 Phase 1 — 3-way sync modal shown when the candidate edits
 * a "cascading" identity field (full_name, professional_title,
 * target_role, location, bio, linkedin, website) inside a document
 * (Resume Builder / Cover Letter Generator).
 *
 * Behavior:
 *  - "Change only this document": save the document with the new
 *    value, profile + other documents are untouched.
 *  - "Update profile and all documents": save the document AND patch
 *    the profile so the next render of every document picks up the
 *    new value via the backend cascade.
 *  - "Cancel": close the modal without saving anything.
 *
 * The parent owns the actual save logic — this component just emits
 * the chosen action.
 *
 * Props:
 *  - open: boolean
 *  - onClose(): closes the modal without acting
 *  - onChooseDocumentOnly(): save doc only
 *  - onChooseSyncAll(): save doc + sync profile (cascades)
 *  - documentLabel: e.g. "Resume" or "Cover Letter"
 *  - changedFields: [{ key, label, current, previous }] — what's different
 */
const FIELD_LABELS = {
  full_name: "Full Name",
  professional_title: "Professional Title",
  target_role: "Target Role",
  location: "Location",
  bio: "Professional Summary",
  linkedin: "LinkedIn",
  website: "Website",
};

export const fieldLabelFor = (key) => FIELD_LABELS[key] || key;

const ProfileSyncModal = ({
  open,
  onClose,
  onChooseDocumentOnly,
  onChooseSyncAll,
  documentLabel = "Document",
  changedFields = [],
  busy = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="sm:max-w-[560px]"
        data-testid="profile-sync-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            Sync these changes everywhere?
          </DialogTitle>
          <DialogDescription>
            You edited identity fields that your master profile uses
            across every document. Choose how widely to apply the
            change.
          </DialogDescription>
        </DialogHeader>

        {changedFields.length > 0 && (
          <div
            className="my-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
            data-testid="profile-sync-modal-changes"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Fields changed in this {documentLabel.toLowerCase()}:
            </p>
            <ul className="space-y-1.5">
              {changedFields.map((f) => (
                <li
                  key={f.key}
                  className="text-sm text-slate-700"
                  data-testid={`profile-sync-modal-change-${f.key}`}
                >
                  <span className="font-medium text-slate-900">
                    {f.label || fieldLabelFor(f.key)}:
                  </span>{" "}
                  <span className="line-through text-slate-400 mr-2">
                    {f.previous || "(empty)"}
                  </span>
                  <span className="text-blue-700">{f.current || "(empty)"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-3 my-2">
          <button
            type="button"
            disabled={busy}
            onClick={onChooseSyncAll}
            className="w-full text-left rounded-xl border-2 border-blue-600 bg-blue-50/40 hover:bg-blue-50 transition-colors px-4 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="profile-sync-modal-sync-all-btn"
          >
            <div className="flex items-start gap-3">
              <Layers className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">
                  Update profile and all documents
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Recommended — keeps your resume, cover letter, and
                  public profile page in sync.
                </p>
              </div>
            </div>
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={onChooseDocumentOnly}
            className="w-full text-left rounded-xl border border-slate-300 hover:border-slate-400 bg-white px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="profile-sync-modal-doc-only-btn"
          >
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">
                  Change only this {documentLabel.toLowerCase()}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Use a different value just for this {documentLabel.toLowerCase()}.
                  Your profile won't change.
                </p>
              </div>
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="profile-sync-modal-cancel-btn"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSyncModal;
